import {useEffect, useState} from 'react';

import {getAgentFoundryCanvasPreviewUrl} from './network/GetAgentFoundryCanvasPreview';
import {getCanvasDefinitionPreviewUrl} from './network/GetCanvasDefinitionPreview';
import {getCanvasFolderArtifactPreviewUrl} from './network/GetCanvasFolderArtifactPreview';
import {getCanvasVersionPreviewUrl} from './network/GetCanvasVersionPreview';
import {getChatCanvasPreviewUrl} from './network/GetChatCanvasPreview';
import {CanvasPreviewErrorReason, getPreviewErrorMessage, getPreviewErrorReason} from './previewErrors';

export type CanvasExpandedPreviewTarget =
  | {
      canvasDefinitionId: string;
      canvasVersionId?: undefined;
      canvasFolderArtifactId?: undefined;
      agentFoundryId?: undefined;
      chatSessionId?: undefined;
      agentFoundryCanvasTabName?: undefined;
    }
  | {
      canvasVersionId: string;
      canvasDefinitionId?: undefined;
      canvasFolderArtifactId?: undefined;
      agentFoundryId?: undefined;
      chatSessionId?: undefined;
      agentFoundryCanvasTabName?: undefined;
    }
  | {
      canvasFolderArtifactId: string;
      canvasDefinitionId?: undefined;
      canvasVersionId?: undefined;
      agentFoundryId?: undefined;
      chatSessionId?: undefined;
      agentFoundryCanvasTabName?: undefined;
    }
  | {
      agentFoundryId: string;
      // 'overview', or the system name of one of the foundry's additional canvas tabs.
      agentFoundryCanvasTabName: string;
      canvasDefinitionId?: undefined;
      canvasVersionId?: undefined;
      canvasFolderArtifactId?: undefined;
      chatSessionId?: undefined;
    }
  | {
      chatSessionId: string;
      canvasVersionId: string;
      canvasDefinitionId?: undefined;
      canvasFolderArtifactId?: undefined;
      agentFoundryId?: undefined;
      agentFoundryCanvasTabName?: undefined;
    };

export interface UseCanvasExpandedPreviewUrlResult {
  signedUrl: string | null;
  isLoading: boolean;
  errorMessage: string | null;
  errorReason: CanvasPreviewErrorReason | null;
}

export default function useCanvasExpandedPreviewUrl(
  target: CanvasExpandedPreviewTarget,
  refreshToken: number = 0
): UseCanvasExpandedPreviewUrlResult {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorReason, setErrorReason] = useState<CanvasPreviewErrorReason | null>(null);
  const {
    canvasDefinitionId,
    canvasVersionId,
    canvasFolderArtifactId,
    agentFoundryId,
    agentFoundryCanvasTabName,
    chatSessionId,
  } = target;

  useEffect(() => {
    let isCancelled = false;

    setIsLoading(true);
    setErrorMessage(null);
    setErrorReason(null);
    setSignedUrl(null);

    let fetchPreviewUrl: Promise<{signedUrl: string}>;
    if (agentFoundryId != null) {
      fetchPreviewUrl = getAgentFoundryCanvasPreviewUrl(agentFoundryId, agentFoundryCanvasTabName);
    } else if (chatSessionId != null && canvasVersionId != null) {
      fetchPreviewUrl = getChatCanvasPreviewUrl(chatSessionId, canvasVersionId);
    } else if (canvasFolderArtifactId != null) {
      fetchPreviewUrl = getCanvasFolderArtifactPreviewUrl(canvasFolderArtifactId);
    } else if (canvasVersionId != null) {
      fetchPreviewUrl = getCanvasVersionPreviewUrl(canvasVersionId);
    } else {
      fetchPreviewUrl = getCanvasDefinitionPreviewUrl(canvasDefinitionId);
    }

    fetchPreviewUrl
      .then((response) => {
        if (!isCancelled) {
          setSignedUrl(response.signedUrl);
        }
      })
      // eslint-disable-next-line benchlint/always-throw-from-catch -- expected API errors are shown inline
      .catch((error) => {
        if (!isCancelled) {
          setErrorMessage(getPreviewErrorMessage(error));
          setErrorReason(getPreviewErrorReason(error));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    agentFoundryCanvasTabName,
    agentFoundryId,
    canvasDefinitionId,
    canvasFolderArtifactId,
    canvasVersionId,
    chatSessionId,
    refreshToken,
  ]);

  return {signedUrl, isLoading, errorMessage, errorReason};
}
